import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FLAME, FLAME_2, INK, BODY, MUTED, BORDER, MONO } from './SiteLayout';

const DARK_2 = '#3a352d';
const HEAD   = '#5a544a';

const cleanDomain = (s) => s.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();

const Stat = ({ big, label, color = INK }) => (
  <div style={{
    flex: '1 1 92px', minWidth: 92, textAlign: 'center',
    background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 10px',
  }}>
    <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1, color }}>{big}</div>
    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.5px', color: HEAD, marginTop: 6, textTransform: 'uppercase' }}>{label}</div>
  </div>
);

const TAG_COLOR = { verified: '#16a34a', invalid: '#b4232a', unverifiable: '#c9820a' };

function RecordRow({ rec, kind }) {
  const [open, setOpen] = useState(false);
  const notes = [...(rec.errors || []), ...(rec.warnings || [])];
  const hasDetail = rec.raw || notes.length > 0;
  return (
    <div style={{ borderBottom: `1px solid ${BORDER}` }}>
      <div
        onClick={() => hasDetail && setOpen(o => !o)}
        style={{
          display: 'grid', gridTemplateColumns: '1.6fr 1fr .7fr .9fr 20px',
          gap: 8, alignItems: 'center', padding: '9px 12px',
          fontFamily: MONO, fontSize: 12, cursor: hasDetail ? 'pointer' : 'default',
        }}
      >
        <span style={{ color: INK, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.domain || '\u2014'}</span>
        <span style={{ color: DARK_2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.publisherAccountId || '\u2014'}</span>
        <span style={{ color: rec.accountType === 'DIRECT' ? '#15803d' : DARK_2, fontWeight: 600 }}>{rec.accountType || '\u2014'}</span>
        <span style={{ color: rec.certAuthorityId ? (TAG_COLOR[rec.tagIdStatus] || DARK_2) : DARK_2 }}>
          {rec.certAuthorityId ? (rec.tagIdStatus || 'has TAG-ID') : '\u2014'}
        </span>
        <span style={{ color: hasDetail ? INK : 'transparent', textAlign: 'center' }}>{open ? '\u25be' : '\u25b8'}</span>
      </div>
      {open && hasDetail && (
        <div style={{ padding: '2px 12px 12px', background: 'rgba(239,236,229,.6)' }}>
          {rec.raw && (
            <div style={{
              fontFamily: MONO, fontSize: 11, color: INK, background: '#fff',
              border: `1px solid ${BORDER}`, borderRadius: 6, padding: '8px 10px',
              overflowX: 'auto', whiteSpace: 'pre', marginBottom: notes.length ? 8 : 0,
            }}>
              L{rec.lineNumber}: {rec.raw}
            </div>
          )}
          {notes.map((n, i) => (
            <div key={i} style={{ fontSize: 12, color: kind === 'invalid' ? '#b4232a' : '#b06f00', lineHeight: 1.5, fontWeight: 500 }}>\u2022 {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, color, rows, kind, open, onToggle }) {
  if (!count) return null;
  return (
    <div style={{
      border: `1.5px solid ${color}`, borderLeft: `4px solid ${color}`,
      borderRadius: 12, overflow: 'hidden', marginTop: 12,
    }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', cursor: 'pointer', background: `${color}0d`,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>{title}</span>
          <span style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 700, color: '#fff',
            background: color, borderRadius: 20, padding: '2px 10px',
          }}>{rows.length}{rows.length !== count ? ` / ${count}` : ''}</span>
        </span>
        <span style={{ color: INK, fontSize: 13, fontWeight: 600 }}>{open ? '\u25be Hide' : '\u25b8 Show'}</span>
      </div>
      {open && (
        <div>
          <div style={{
            display: 'grid', gridTemplateColumns: '1.6fr 1fr .7fr .9fr 20px', gap: 8,
            padding: '8px 12px', borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
            fontFamily: MONO, fontSize: 10, letterSpacing: '.5px', color: HEAD, textTransform: 'uppercase',
            fontWeight: 700, background: 'rgba(239,236,229,.7)',
          }}>
            <span>SSP / Exchange</span><span>Account ID</span><span>Type</span><span>TAG-ID</span><span />
          </div>
          <div style={{ maxHeight: 340, overflowY: 'auto', background: '#fff' }}>
            {rows.length === 0
              ? <div style={{ padding: '16px 12px', fontFamily: MONO, fontSize: 12, color: HEAD }}>No matches for this filter.</div>
              : rows.map((r, i) => <RecordRow key={i} rec={r} kind={kind} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdsTxtCheckerEmbed() {
  const [mode, setMode] = useState('domain');
  const [value, setValue] = useState('');
  const [type, setType] = useState('ads.txt');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [openSections, setOpenSections] = useState({ valid: false, invalid: false, dup: false });

  const run = async () => {
    const raw = value.trim();
    if (!raw) { setError(mode === 'domain' ? 'Enter a domain, e.g. example.com' : 'Enter a bundle ID or store URL'); return; }
    setLoading(true); setError(''); setResult(null); setQuery('');
    setOpenSections({ valid: false, invalid: false, dup: false });
    try {
      const url = mode === 'domain' ? '/api/public/ads-txt/check' : '/api/public/ads-txt/check-by-bundle';
      const body = mode === 'domain'
        ? { domain: cleanDomain(raw), type }
        : { input: raw, type: type === 'ads.txt' ? 'ads.txt' : 'app-ads.txt' };
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.success === false) setError(data.error || 'No file found for that input.');
      else setResult(data);
    } catch {
      setError('Something went wrong reaching the checker. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') run(); };
  const s = result?.summary;
  const resv = result?.resolution;

  const match = (r) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (r.domain || '').toLowerCase().includes(q)
      || (r.publisherAccountId || '').toLowerCase().includes(q)
      || (r.certAuthorityId || '').toLowerCase().includes(q)
      || (r.accountType || '').toLowerCase().includes(q);
  };
  const validRows   = useMemo(() => (result?.records || []).filter(match),       [result, query]);
  const invalidRows = useMemo(() => (result?.invalidRecords || []).filter(match), [result, query]);
  const dupRows     = useMemo(() => (result?.duplicates || []).filter(match),     [result, query]);

  const setAll = (v) => setOpenSections({ valid: v, invalid: v, dup: v });
  const toggle = (k) => setOpenSections(o => ({ ...o, [k]: !o[k] }));

  const tabStyle = (active) => ({
    flex: 1, textAlign: 'center', fontFamily: MONO, fontSize: 13, fontWeight: 600,
    padding: '10px 12px', cursor: 'pointer', border: 'none', background: 'none',
    color: active ? INK : HEAD,
    borderBottom: active ? `2px solid ${FLAME}` : `2px solid transparent`,
  });

  const smallBtn = {
    fontFamily: MONO, fontSize: 12, fontWeight: 600, color: INK,
    background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8,
    padding: '8px 12px', cursor: 'pointer',
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,.92)', border: `1px solid ${BORDER}`,
      borderRadius: 18, padding: '28px', boxShadow: '0 20px 50px -30px rgba(40,30,18,.35)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '1px', color: FLAME, textTransform: 'uppercase' }}>Free tool</span>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '1px', color: '#15803d', border: '1px solid #bfe3c8', background: '#eefaf0', padding: '3px 8px', borderRadius: 20 }}>NO SIGN-UP</span>
      </div>
      <h3 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 4px', letterSpacing: '-.4px' }}>Ads.txt / App-ads.txt Checker</h3>
      <p style={{ fontSize: 15, color: BODY, margin: '0 0 18px', lineHeight: 1.5 }}>
        Validate any publisher's ads.txt \u2014 full record breakdown, DIRECT vs RESELLER, duplicates, and TAG-ID checks against each SSP's sellers.json.
      </p>

      <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER}`, marginBottom: 16 }}>
        <button style={tabStyle(mode === 'domain')} onClick={() => { setMode('domain'); setResult(null); setError(''); }}>Check by Domain</button>
        <button style={tabStyle(mode === 'bundle')} onClick={() => { setMode('bundle'); setResult(null); setError(''); setType('app-ads.txt'); }}>Check by In-app Bundle</button>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={onKey}
          placeholder={mode === 'domain' ? 'example.com' : 'com.king.candycrushsaga  or  App Store URL'}
          aria-label={mode === 'domain' ? 'Domain to check' : 'Bundle ID or store URL'}
          style={{ flex: '1 1 240px', minWidth: 0, fontFamily: MONO, fontSize: 14, padding: '12px 14px', borderRadius: 10, border: `1px solid ${BORDER}`, background: '#fff', color: INK }}
        />
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="File type"
          style={{ fontFamily: MONO, fontSize: 13, padding: '12px 14px', borderRadius: 10, border: `1px solid ${BORDER}`, background: '#fff', color: INK, cursor: 'pointer' }}>
          <option value="ads.txt">ads.txt</option>
          <option value="app-ads.txt">app-ads.txt</option>
        </select>
        <button onClick={run} disabled={loading} className="ia-btn"
          style={{ flex: 'none', background: INK, color: '#f6f5f2', border: 'none', fontSize: 15, fontWeight: 600, padding: '12px 26px', borderRadius: 10, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Checking\u2026' : 'Check'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 16, fontSize: 14, color: '#b4232a', background: '#fdecec', border: '1px solid #f5c2c2', borderRadius: 10, padding: '12px 14px', fontWeight: 500 }}>{error}</div>
      )}

      {s && (
        <div style={{ marginTop: 20 }}>
          {resv && (
            <div style={{ fontFamily: MONO, fontSize: 12, color: INK, marginBottom: 12, lineHeight: 1.5 }}>
              Resolved {resv.appName ? <strong>{resv.appName}</strong> : resv.inputNormalised}{' '}\u2192 <strong>{resv.domain}</strong>
              {resv.source ? <span style={{ color: HEAD }}> (via {resv.source})</span> : null}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: s.validRecords > 0 ? '#15803d' : '#b4232a' }}>
              {s.validRecords > 0 ? '\u2713 Valid file found' : '\u2717 No valid records'}
            </span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: HEAD }}>{result.domain}/{result.type}</span>
            {result.recordsTruncated && (
              <span style={{ fontFamily: MONO, fontSize: 11, color: '#b06f00', fontWeight: 600 }}>\u00b7 showing first 5000 of {result.totalRecordsBeforeTruncation}</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Stat big={s.totalRecords} label="Records" />
            <Stat big={s.validRecords} label="Valid" color="#15803d" />
            <Stat big={s.invalidRecords} label="Invalid" color={s.invalidRecords > 0 ? '#b4232a' : INK} />
            <Stat big={s.directEntries} label="Direct" />
            <Stat big={s.resellerEntries} label="Reseller" />
            <Stat big={s.uniqueSsps} label="SSPs" />
            <Stat big={s.duplicates} label="Duplicates" color={s.duplicates > 0 ? FLAME : INK} />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SSP domain, account ID or TAG-ID\u2026"
              aria-label="Search records"
              style={{ flex: '1 1 260px', minWidth: 0, fontFamily: MONO, fontSize: 13, padding: '10px 14px', borderRadius: 10, border: `1px solid ${BORDER}`, background: '#fff', color: INK }}
            />
            <button style={smallBtn} onClick={() => setAll(true)}>Expand all</button>
            <button style={smallBtn} onClick={() => setAll(false)}>Collapse all</button>
          </div>

          <Section title="Valid records"   count={s.validRecords}   color="#15803d" rows={validRows}   kind="valid"   open={openSections.valid}   onToggle={() => toggle('valid')} />
          <Section title="Invalid records" count={s.invalidRecords} color="#b4232a" rows={invalidRows} kind="invalid" open={openSections.invalid} onToggle={() => toggle('invalid')} />
          <Section title="Duplicates"      count={s.duplicates}     color={FLAME}   rows={dupRows}     kind="dup"     open={openSections.dup}     onToggle={() => toggle('dup')} />

          <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 12, background: 'rgba(239,236,229,.85)', border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: BODY, lineHeight: 1.5 }}>
              Run this across your whole inventory automatically, with monitoring and alerts, in the AdTech Hub.
            </span>
            <Link to="/contact" className="ia-btn" style={{ flex: 'none', background: FLAME, color: INK, fontSize: 14, fontWeight: 600, padding: '10px 20px', borderRadius: 24, textDecoration: 'none' }}>Start free \u2192</Link>
          </div>
        </div>
      )}
    </div>
  );
}
